import { useCallback, useEffect, useMemo, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'

import { Table, DownloadButtonCell, MobileSortBy } from 'components/Table'
import { DATETIME_SHORT, formatTime } from 'app/utils/formatDate'
import {
  CloudSoftwareHistoryEnum,
  HomeSoftwareType,
  SoftwareHistoryRequestedByEnum,
} from 'features/home/types'
import { SoftwareVersionCell } from '../HomeSoftwareUpdates/components/SoftwareVersionCell'
import { hasSystemAccessEnabled, releaseSetFilename } from 'features/home/utils'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  homeConfigSelectors,
  selectedHomeSelector,
} from 'features/home/selectors'
import {
  AvailableSoftwareActionCell,
  AvailableSoftwareErrorMessage,
  AvailableSoftwareWarnings,
} from './styles'
import HomeSoftwareUpdatesMobile from '../HomeSoftwareUpdates/components/HomeSoftwareUpdatesMobile'
import { fetchCloudSoftwareHistoryThunk } from '../HomeCloudSoftwareHistory/slice'
import { getSortOptions } from 'components/Table/utils'

import { TableWithPaginationWrapper } from 'components/Table/styles'
import {
  cloudSoftwareHistorySelector,
  selectSoftwareUpdateInProgress,
  selectSoftwareUpdatesInProgress,
} from '../HomeCloudSoftwareHistory/selectors'
import { CommonLinkButton } from 'components/Button'
import SoftwareUpdateStatus from '../HomeCloudSoftwareHistory/components/UpdateStatus'
import {
  homeRuntimeMeetsMinimumRuntimeRequirements,
  useHomeNotConfigAccessEnabled,
  useHostIsOffline,
  usePermissionLogic,
} from '../utils'
import Tooltip from 'components/Tooltip'
import Icon from '@mdi/react'
import { mdiAlert } from '@mdi/js'
import theme from 'theme_'
import { requiresRuntimeLicense } from '../const'
import { installHomeSoftwareUpdateThunk } from '../slice'

enum softwareUpdatesHeaders {
  released = 'created',
  version = 'version',
}

interface HomeAvailableSoftwareUpdatesTableProps {
  data: HomeSoftwareType[]
}

const DEFAULT_SORT_BY = { id: 'created', desc: true }

const VersionCell = (cell: Cell<HomeSoftwareType>) => {
  return <SoftwareVersionCell version={cell.value.name} />
}

interface SoftwareUpdateActionsCellProps {
  software: HomeSoftwareType
  isOnlyWarnShown?: boolean
}

export const SoftwareUpdateActionsCell = ({
  software,
  isOnlyWarnShown,
}: SoftwareUpdateActionsCellProps) => {
  const [warnings, setWarnings] = useState<Array<string>>([])
  const [isLoading, setIsLoading] = useState(false)
  const home = useAppSelector(selectedHomeSelector.data)
  const hostConfig = useAppSelector(homeConfigSelectors.data)
  const cloudSoftwareHistory = useAppSelector(cloudSoftwareHistorySelector)
  const isSoftwareUpdateInProgress = useAppSelector(
    selectSoftwareUpdateInProgress
  )
  const homeDoesNotSupportRemoteInstalls = usePermissionLogic(
    true,
    homeRuntimeMeetsMinimumRuntimeRequirements(home)
  )
  const hostIsOffline = useHostIsOffline()
  const homeConfigAccessDisabled = useHomeNotConfigAccessEnabled()

  useEffect(() => {
    const warn = []

    if (homeDoesNotSupportRemoteInstalls) {
      warn.push(
        'First use System Monitor to install SavantOS 8.6 or higher to support remote installations.'
      )
    }
    if (hostIsOffline && !isSoftwareUpdateInProgress) {
      warn.push(`${home?.name} must be online.`)
    }
    if (homeConfigAccessDisabled) {
      warn.push('Request home configuration access.')
    }

    const homeMajor = home?.softwareInfo?.version?.major || 0

    const blockNeedsOSUpdate =
      home?.hostType === "Mac" &&
      home?.softwareInfo.version.number < 1100000200000 &&
      software.version.number > 1100000400000

    if (blockNeedsOSUpdate) {
      warn.push('The selected SavantOS update requires a macOS upgrade on this Mac Host. Please update your Mac Host to the required macOS version and complete this runtime update from System Monitor.')
    }


    const blockMajorUpgrade =
      homeMajor <= 9 &&
      homeMajor < software.version.major &&
      (typeof hostConfig?.hostMachineType !== 'number' ||
        requiresRuntimeLicense.has(hostConfig.hostMachineType))

    if (blockMajorUpgrade) {
      warn.push(`Use System Monitor to license and upgrade ${home?.name}.`)
    }

    setWarnings(warn)
  }, [
    home?.name,
    home?.softwareInfo.version,
    home?.hostType,
    homeConfigAccessDisabled,
    homeDoesNotSupportRemoteInstalls,
    hostConfig?.hostMachineType,
    hostIsOffline,
    software.version,
    isSoftwareUpdateInProgress,
  ])

  const softwareUpdatesInProgress = useAppSelector(
    selectSoftwareUpdatesInProgress
  )

  const mostRecentMatchingUpdate =
    cloudSoftwareHistory &&
    cloudSoftwareHistory.find(
      (update) => update.versionNumber === software.version.number
    )

  const installingThisSoftware = softwareUpdatesInProgress.find((e) => {
    if (e.versionNumber === mostRecentMatchingUpdate?.versionNumber) {
      return !(
        e.requestedBy === SoftwareHistoryRequestedByEnum.home &&
        (e.state === CloudSoftwareHistoryEnum.download ||
          e.state === CloudSoftwareHistoryEnum.ready)
      )
    }

    return false
  })

  const dispatch = useAppDispatch()

  const hasSystemAccess = hasSystemAccessEnabled(home)

  const handleInstall = useCallback(async () => {
    if (home?.id) {
      try {
        setIsLoading(true)
        await dispatch(
          installHomeSoftwareUpdateThunk(home.id, software.partNumber, {
            componentSoftwareId: software.id,
            clearConfig: false,
          })
        )

        dispatch(
          fetchCloudSoftwareHistoryThunk({
            homeId: home.id,
            partNumber: home.hostType,
          })
        )
        setIsLoading(false)
      } catch (e) {
        console.log('Error install home software update', e)
        setIsLoading(false)
        throw e
      }
    }
  }, [
    home?.id,
    home?.hostType,
    setIsLoading,
    dispatch,
    software.partNumber,
    software.id,
  ])

  const makeWarningTooltipText = useMemo(() => {
    const warningsItems = warnings.map((warning) => {
      return <li key={warning}>{warning}</li>
    })

    return (
      <AvailableSoftwareErrorMessage>
        <p>To install SavantOS {software.version.name}:</p>
        <AvailableSoftwareWarnings>{warningsItems}</AvailableSoftwareWarnings>
      </AvailableSoftwareErrorMessage>
    )
  }, [software.version.name, warnings])

  // TODO: find out `fileName` fallback if `releaseSetFilename` doesn't match
  const fileName =
    releaseSetFilename(software.location.url) ?? software.partNumber

  if (isOnlyWarnShown)
    return (
      <>
        {warnings.length > 0 && (
          <Tooltip
            content={makeWarningTooltipText}
            showOn="click"
            align="end"
            positions={['bottom']}
          >
            <Icon
              path={mdiAlert}
              size={0.9}
              color={theme.colors.horizonOrange}
            />
          </Tooltip>
        )}
      </>
    )

  return (
    <AvailableSoftwareActionCell>
      {warnings.length > 0 && (
        <Tooltip
          content={makeWarningTooltipText}
          showOn="click"
          align="end"
          positions={['bottom']}
        >
          <Icon path={mdiAlert} size={0.9} color={theme.colors.horizonOrange} />
        </Tooltip>
      )}
      {hasSystemAccess &&
        warnings.length === 0 &&
        (installingThisSoftware ? (
          <SoftwareUpdateStatus
            state={installingThisSoftware.state}
            message={installingThisSoftware.message}
            progress={installingThisSoftware.progress}
          />
        ) : (
          !isSoftwareUpdateInProgress && (
            <CommonLinkButton
              disabled={softwareUpdatesInProgress.length > 0 || isLoading}
              onClick={handleInstall}
            >
              Install
            </CommonLinkButton>
          )
        ))}
      <DownloadButtonCell url={software.location.url} fileName={fileName} />
    </AvailableSoftwareActionCell>
  )
}

const DateCell = (cell: Cell) => formatTime(cell?.value * 1000, DATETIME_SHORT)

function HomeAvailableSoftwareUpdatesTable(
  props: HomeAvailableSoftwareUpdatesTableProps
) {
  const { data } = props

  const headers = useMemo(
    () => ({
      released: {
        label: 'Released',
        id: softwareUpdatesHeaders.released,
      },
      version: {
        label: 'Version',
        id: softwareUpdatesHeaders.version,
      },
    }),
    []
  )

  const columns: readonly Column<HomeSoftwareType>[] = useMemo(
    () => [
      {
        Header: 'Version',
        accessor: softwareUpdatesHeaders.version,
        Cell: VersionCell,
      },
      {
        Header: 'Released',
        accessor: softwareUpdatesHeaders.released,
        Cell: DateCell,
      },
      {
        Header: '',
        accessor: 'id',
        disableSortBy: true,
        id: 'actions',
        Cell: ({ row: { original } }: Cell<HomeSoftwareType>) => (
          <SoftwareUpdateActionsCell software={original} />
        ),
      },
    ],
    []
  )

  const sortOptions = useMemo(() => getSortOptions(headers, true), [headers])
  const defaultSortBy = sortOptions[0]
  const [sortBy, setSortBy] =
    useState<SortingRule<HomeSoftwareType>>(defaultSortBy)

  return (
    <TableWithPaginationWrapper>
      <MobileSortBy
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
      <Table
        columns={columns}
        data={data}
        defaultSortBy={DEFAULT_SORT_BY}
        mobileViewComponent={HomeSoftwareUpdatesMobile}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
    </TableWithPaginationWrapper>
  )
}

export default HomeAvailableSoftwareUpdatesTable
